import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, DocumentTextIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../../../contexts/SettingsContext';
import type { LegalDocument } from '../../../../types/settings';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const schema = z.object({
  type: z.string().min(1, 'Type is required'),
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Content is required'),
  version: z.string().min(1, 'Version is required'),
  is_active: z.boolean().default(true),
  effective_date: z.string().min(1, 'Effective date is required'),
});

type FormData = z.infer<typeof schema>;

const DOC_TYPES: Record<string, string> = {
  privacy_policy: 'Privacy Policy',
  terms_of_service: 'Terms of Service',
  cookie_policy: 'Cookie Policy',
  disclaimer: 'Disclaimer',
  refund_policy: 'Refund Policy',
  other: 'Other',
};

type View = 'list' | 'form' | 'preview';

export default function MobileSettingsLegal() {
  const { state, addLegalDocument, updateLegalDocument, deleteLegalDocument } = useSettings();
  const [view, setView] = useState<View>('list');
  const [editingDoc, setEditingDoc] = useState<LegalDocument | null>(null);
  const [previewDoc, setPreviewDoc] = useState<LegalDocument | null>(null);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { type: '', title: '', content: '', version: '1.0', is_active: true, effective_date: new Date().toISOString().split('T')[0] },
  });

  const openAdd = () => {
    setEditingDoc(null);
    reset({ type: '', title: '', content: '', version: '1.0', is_active: true, effective_date: new Date().toISOString().split('T')[0] });
    setView('form');
  };

  const openEdit = (doc: LegalDocument) => {
    setEditingDoc(doc);
    reset({ type: doc.type, title: doc.title, content: doc.content, version: doc.version, is_active: doc.is_active, effective_date: doc.effective_date });
    setView('form');
  };

  const openPreview = (doc: LegalDocument) => {
    setPreviewDoc(doc);
    setView('preview');
  };

  const onSubmit = async (data: FormData) => {
    if (editingDoc) {
      await updateLegalDocument({ ...editingDoc, ...data });
    } else {
      await addLegalDocument(data);
    }
    setView('list');
    reset();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this legal document?')) return;
    await deleteLegalDocument(id);
  };

  if (view === 'preview' && previewDoc) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-golf-gray text-base">{previewDoc.title}</h3>
          <button onClick={() => setView('list')} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">v{previewDoc.version}</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {new Date(previewDoc.effective_date).toLocaleDateString()}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${previewDoc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {previewDoc.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 prose prose-sm max-w-none">
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(previewDoc.content) as string) }} />
        </div>
      </div>
    );
  }

  if (view === 'form') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-golf-gray text-base">{editingDoc ? 'Edit Document' : 'New Document'}</h3>
          <button onClick={() => setView('list')} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
            <select {...register('type')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange">
              <option value="">Select type</option>
              {Object.entries(DOC_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {errors.type && <p className="text-xs text-red-500 mt-1">{errors.type.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input type="text" {...register('title')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Version *</label>
              <input type="text" {...register('version')} placeholder="1.0" className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
              {errors.version && <p className="text-xs text-red-500 mt-1">{errors.version.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Effective Date *</label>
              <input type="date" {...register('effective_date')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
              {errors.effective_date && <p className="text-xs text-red-500 mt-1">{errors.effective_date.message}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_active" {...register('is_active')} className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange" />
            <label htmlFor="is_active" className="text-sm text-gray-700">Active document</label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Content (Markdown) *</label>
            <textarea
              {...register('content')}
              rows={10}
              placeholder="Enter content in Markdown format..."
              className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange font-mono"
            />
            {errors.content && <p className="text-xs text-red-500 mt-1">{errors.content.message}</p>}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setView('list')}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (editingDoc ? 'Update' : 'Add Document')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const grouped = state.legalDocuments.reduce((acc, doc) => {
    if (!acc[doc.type]) acc[doc.type] = [];
    acc[doc.type].push(doc);
    return acc;
  }, {} as Record<string, LegalDocument[]>);

  return (
    <div className="space-y-4">
      <button
        onClick={openAdd}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-golf-orange text-white rounded-xl text-sm font-medium"
      >
        <PlusIcon className="w-4 h-4" />
        Add Document
      </button>

      {state.legalDocuments.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
          <DocumentTextIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No legal documents yet</p>
        </div>
      ) : (
        Object.entries(grouped).map(([type, docs]) => (
          <div key={type} className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
              {DOC_TYPES[type] ?? type}
            </p>
            {docs.map(doc => (
              <div key={doc.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-golf-gray truncate">{doc.title}</p>
                    <p className="text-xs text-gray-400">v{doc.version} · {new Date(doc.effective_date).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${doc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {doc.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openPreview(doc)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg">
                    <EyeIcon className="w-3.5 h-3.5" /> Preview
                  </button>
                  <button onClick={() => openEdit(doc)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-golf-orange bg-golf-orange/10 rounded-lg">
                    <PencilIcon className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDelete(doc.id)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg">
                    <TrashIcon className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
